import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpDown, Users } from "lucide-react";
import { useEffect, useState } from "react";

import { MembersList } from "../../../../components/club/MembersList";
import { Breadcrumbs, SearchInput, Select } from "../../../../components/ui";
import { trpc } from "../../../../lib/trpc";

const ROLE_OPTIONS = [
  { value: "", label: "All Roles" },
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "moderator", label: "Moderator" },
  { value: "member", label: "Member" },
];

const SORT_OPTIONS = [
  { value: "", label: "Default" },
  { value: "elo", label: "ELO" },
  { value: "matches", label: "Matches Played" },
  { value: "lastActive", label: "Last Active" },
  { value: "username", label: "Alphabetical" },
];

type ClubRoleValue = "owner" | "admin" | "moderator" | "member";

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export const Route = createFileRoute("/app/clubs/$slug/members")({
  component: MembersPage,
});

function MembersPage() {
  const { slug } = Route.useParams();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [roleFilter, setRoleFilter] = useState("");
  const [sortBy, setSortBy] = useState("");
  const { data: club, isLoading } = trpc.club.get.useQuery({ slug });

  if (isLoading || !club) return <MembersPageSkeleton />;

  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div className="space-y-3">
        <Breadcrumbs
          items={[
            { label: club.name, to: "/app/clubs/$slug", params: { slug } },
            { label: "Members" },
          ]}
        />
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-white tracking-tight">Members</h1>
            <div className="flex items-center gap-2 text-sm text-surface-500 lg:hidden">
              <span>{club.memberCount.toLocaleString()} members</span>
              {club.onlineMemberCount > 0 && (
                <>
                  <span className="text-surface-600">·</span>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
                  <span className="text-green-400">{club.onlineMemberCount} online</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar: search + filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            placeholder="Search members..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onClear={() => setSearch("")}
          />
        </div>
        <div className="flex gap-2">
          <Select
            options={ROLE_OPTIONS}
            value={roleFilter}
            onChange={setRoleFilter}
            placeholder="All Roles"
            icon={<Users className="size-4 text-surface-500" />}
            className="w-44"
          />
          <Select
            options={SORT_OPTIONS}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort"
            icon={<ArrowUpDown className="size-4 text-surface-500" />}
            className="w-48"
          />
        </div>
      </div>

      <MembersList
        clubId={club.id}
        search={debouncedSearch}
        role={roleFilter as ClubRoleValue | ""}
        sortBy={sortBy}
      />
    </div>
  );
}

function MembersPageSkeleton() {
  return (
    <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="space-y-1.5">
          <div className="h-6 w-28 rounded bg-surface-700/40 animate-pulse" />
          <div className="h-3.5 w-16 rounded bg-surface-700/25 animate-pulse" />
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1 h-9 rounded-lg bg-surface-700/30 animate-pulse" />
        <div className="flex gap-2">
          <div className="h-9 w-36 rounded-lg bg-surface-700/30 animate-pulse" />
          <div className="h-9 w-40 rounded-lg bg-surface-700/30 animate-pulse" />
        </div>
      </div>
      <div className="rounded-xl border border-surface-800/40 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-3 px-5 py-3 border-b border-separator">
            <div className="w-8 h-8 rounded-lg bg-surface-700/40 animate-pulse" />
            <div className="flex-1 space-y-1.5">
              <div className="h-3.5 w-24 rounded bg-surface-700/40 animate-pulse" />
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-surface-700/30 animate-pulse" />
              <div className="h-3.5 w-10 rounded bg-surface-700/30 animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
